/**
 * /me/travel/reconciliation — Session 3d PR 26.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { TravelTripItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyTravelReconciliationPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-travel-reconciliation", employeeId],
    queryFn: () => session3dApi.listMyTrips(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<TravelTripItem>[] = [
    {
      headerKey: "me.travel-reconciliation.col.destination",
      label: "Destination",
      render: (r) => <span className="font-medium">{r.destination}</span>,
    },
    {
      headerKey: "me.travel-reconciliation.col.dates",
      label: "Dates",
      render: (r) => <span dir="ltr" className="font-mono text-xs">{r.departure_date} → {r.return_date}</span>,
    },
    {
      headerKey: "me.travel-reconciliation.col.purpose",
      label: "Purpose",
      render: (r) => <span className="text-xs text-text-secondary">{r.purpose}</span>,
    },
    {
      headerKey: "me.travel-reconciliation.col.per_diem",
      label: "Per-diem received (SAR)",
      render: (r) => <span className="font-mono text-xs">{(r.per_diem_received_sar ?? 0).toFixed(2)}</span>,
    },
  ];

  return (
    <MeListPage<TravelTripItem>
      title={t("me.travel-reconciliation.title", "Pending Reconciliation (My Trips)")}
      subtitle={t("me.travel-reconciliation.subtitle", "Submit travel expenses post-return.")}
      domain="travel-reconciliation"
      khidmatPrefill={t("me.travel-reconciliation.ask_khidmat", "Help me submit my travel expenses.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.trip_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.travel-reconciliation.title", "Pending Reconciliation (My Trips)"),
        window_label: t("me.travel-reconciliation.window", "pending submission"),
      }}
      emptyHeadline={t("me.travel-reconciliation.empty_headline", "No pending reconciliations.")}
      emptyBody={t("me.travel-reconciliation.empty_body", "Khidmat: when you return from a trip, the reconciliation form appears here.")}
    />
  );
}
